const getHost = () => {
  const requestEvent = useRequestEvent();
  if (requestEvent) {
    return requestEvent.node.req.headers.host;
  }

  return window.location.host;
};

const TOKEN_REFRESH_BUFFER_SECONDS = 60;

const isTokenExpired = (token) => {
  try {
    const payload = token.split('.')[1];

    let decodedPayload;
    if (typeof Buffer !== 'undefined') {
      decodedPayload = Buffer.from(payload, 'base64').toString();
    } else {
      decodedPayload = window.atob(payload);
    }

    const { exp } = JSON.parse(decodedPayload);

    return Date.now() >= (exp - TOKEN_REFRESH_BUFFER_SECONDS) * 1000;
  } catch (err) {
    console.error('Invalid token:', err);
    return true;
  }
};

let refreshInFlight = null;

const getBaseURL = () => {
  return `${useRequestURL().protocol}//${getHost().replace(useRuntimeConfig().public.port, useRuntimeConfig().public.apiPort)}/api`;
};

const getToken = async({ baseURL }) => {
  const embed = import.meta.client && useEmbed().value;
  let token = embed ? useAuthStore().token : useCookie('token').value;
  const refresh = embed ? useAuthStore().refresh : useCookie('refresh').value;

  if (token && refresh && isTokenExpired(token)) {
    refreshInFlight = refreshInFlight || $fetch('/public/users/refresh', {
      baseURL,
      method: 'POST',
      body: { refresh },
    }).catch(() => null).finally(() => { refreshInFlight = null });
    const refreshResponse = await refreshInFlight;

    if (refreshResponse) {
      useAuthStore().setAuth(refreshResponse);
      token = useAuthStore().token;
    }
  }

  return token;
};

const request = (method) => {
  return async(url, options) => {
    const baseURL = getBaseURL();
    const token = await getToken({ baseURL });

    const requestOptions = {
      method,
      baseURL,
      ...options,
      headers: {
        ...(token ? { Authorization: token } : {}),
        ...(useNuxtApp?.().$replay ? { 'x-sentry-replay-id': useNuxtApp().$replay?.getReplayId?.() } : {}),
        ...(options?.headers || {}),
      },

      onResponse({ response }) {
        if (import.meta.client && method !== 'GET' && response?.ok && useEmbed().value && window.parent !== window) {
          const urlStr = String(url);
          // Only match POST to the events collection root (/events or /events?…), not sub-routes
          // like /events/:id/stages. The AI-builder flow that starts from the prompt element
          // eventually lands here too.
          if (method === 'POST' && /^\/?events\/?(\?|$)/.test(urlStr) && response._data?._id) {
            postToEmbedParent({
              type: EMBED_MESSAGE.EVENT_CREATED,
              eventId: response._data._id,
              referenceId: response._data.referenceId ?? null,
            });
          }
        }
      },

      onResponseError({ response }) {
        if (response?.status === 401) {
          if (import.meta.client && useEmbed().value) {
            useEmbedAuthFailed().value = true;
            postToEmbedParent({ type: EMBED_MESSAGE.AUTH_FAILED });
            return;
          }
          useAuthStore().logout();
        }
      },
    };

    return $fetch(url, requestOptions);
  };
};

export default {
  delete: request('DELETE'),
  get: request('GET'),
  patch: request('PATCH'),
  post: request('POST'),
  put: request('PUT'),

  getToken,
  getBaseURL,
};
